import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { API, getAuthHeader } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Calendar, Download, Printer, BarChart3, TrendingUp, Users,
  Globe, Truck, ChevronDown, RefreshCw, ArrowUpRight, ArrowDownRight,
  Minus, Link2, CalendarRange, Search, X,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line,
} from 'recharts';

const CHART_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#ec4899', '#14b8a6', '#a855f7'];

const TABS = [
  { id: 'clientCode', label: 'Client Code', icon: BarChart3 },
  { id: 'country', label: 'Country', icon: Globe },
  { id: 'service', label: 'Service', icon: Truck },
  { id: 'topCustomers', label: 'Top 50 Customers', icon: Users },
];

const PRESETS = [
  { id: '7d', label: 'Last 7 Days', days: 7 },
  { id: '14d', label: 'Last 14 Days', days: 14 },
  { id: '30d', label: 'Last 30 Days', days: 30 },
  { id: 'month', label: 'This Month', days: 0 },
  { id: '90d', label: 'Last 90 Days', days: 90 },
];

const fmtDate = (s) => {
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split('T')[0]; };
const monthStart = () => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];

const ManagementReports = () => {
  const today = new Date().toISOString().split('T')[0];
  const [dateFrom, setDateFrom] = useState(daysAgo(30));
  const [dateTo, setDateTo] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('clientCode');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [clientFilter, setClientFilter] = useState('');
  const [selectedClients, setSelectedClients] = useState([]);
  const printRef = useRef(null);

  const dateLabel = useMemo(() => `${fmtDate(dateFrom)} — ${fmtDate(dateTo)}`, [dateFrom, dateTo]);

  const generate = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/reports/management/generate?date_from=${dateFrom}&date_to=${dateTo}`,
        { headers: getAuthHeader() }
      );
      setData(res.data);
      setSelectedClients([]);
      if (res.data.current_total === 0 && res.data.previous_total === 0) {
        toast.info('No data found for selected period');
      } else {
        toast.success(`Report loaded: ${res.data.current_total} parcels`);
      }
    } catch {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  const downloadExcel = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/reports/management/excel?date_from=${dateFrom}&date_to=${dateTo}`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `Management_Report.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch { toast.error('Download failed'); }
  }, [dateFrom, dateTo]);

  const handlePrint = useCallback(() => {
    if (!printRef.current) return;
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Management Report</title>
      <style>body{font-family:Calibri,sans-serif;padding:20px;color:#1e293b}
      table{border-collapse:collapse;width:100%;margin:16px 0}
      th,td{border:1px solid #cbd5e1;padding:6px 10px;text-align:center;font-size:12px}
      th{background:#1B4F72;color:white}
      .up{color:#16a34a}.down{color:#dc2626}
      h2{color:#1B4F72;margin:16px 0 8px}
      </style></head><body>${printRef.current.innerHTML}</body></html>`);
    w.document.close(); w.print();
  }, []);

  const copyLink = useCallback(() => {
    const url = `${window.location.origin}${window.location.pathname}?panel=reports&view=management&from=${dateFrom}&to=${dateTo}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Link copied'));
  }, [dateFrom, dateTo]);

  const applyPreset = (p) => {
    if (p.id === 'month') setDateFrom(monthStart());
    else setDateFrom(daysAgo(p.days));
    setDateTo(today);
    setShowDatePicker(false);
  };

  return (
    <div className="flex flex-col h-full max-md:h-auto" data-testid="management-reports">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 bg-white rounded-xl shadow-sm border border-slate-200 px-4 py-3 mb-3 flex-wrap">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-5 h-5 text-teal-600" />
          <h2 className="text-base font-semibold text-slate-900">Management Reports</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Date Range Selector */}
          <div className="relative">
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 max-w-[300px]"
              data-testid="mgmt-date-picker-btn"
            >
              <CalendarRange className="w-4 h-4 text-teal-500 flex-shrink-0" />
              <span className="truncate">{dateLabel}</span>
              <ChevronDown className="w-3 h-3 flex-shrink-0" />
            </button>
            {showDatePicker && (
              <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 p-3 z-30 min-w-[260px]">
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {PRESETS.map(p => (
                    <button key={p.id} onClick={() => applyPreset(p)} data-testid={`mgmt-preset-${p.id}`}
                      className="px-2.5 py-1 rounded-md text-xs font-medium bg-teal-50 text-teal-700 hover:bg-teal-100"
                    >{p.label}</button>
                  ))}
                </div>
                <div className="space-y-2">
                  <label className="block text-xs text-slate-500 font-medium">From</label>
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-400" />
                  <label className="block text-xs text-slate-500 font-medium">To</label>
                  <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-400" />
                  <Button size="sm" className="w-full bg-teal-600 hover:bg-teal-700 text-white mt-1"
                    onClick={() => setShowDatePicker(false)}>Apply Range</Button>
                </div>
              </div>
            )}
          </div>
          <Button onClick={generate} disabled={loading} className="bg-teal-600 hover:bg-teal-700 text-white" size="sm" data-testid="mgmt-generate-btn">
            {loading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-1" />}
            {loading ? 'Loading...' : 'Generate'}
          </Button>
          {data && data.current_total > 0 && (
            <>
              <Button variant="outline" size="sm" onClick={downloadExcel} data-testid="mgmt-excel-btn"><Download className="w-4 h-4 mr-1" />Excel</Button>
              <Button variant="outline" size="sm" onClick={handlePrint} data-testid="mgmt-print-btn"><Printer className="w-4 h-4 mr-1" />Print</Button>
              <Button variant="outline" size="sm" onClick={copyLink}><Link2 className="w-4 h-4 mr-1" />Share</Button>
            </>
          )}
        </div>
      </div>

      {!data ? (
        <div className="flex-1 flex items-center justify-center bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="text-center">
            <TrendingUp className="w-14 h-14 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700 mb-1">Management Reports</h3>
            <p className="text-sm text-slate-400 mb-4">Select a date range and generate comparison analytics</p>
            <Button onClick={generate} disabled={loading} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="mgmt-generate-empty-btn">
              <TrendingUp className="w-4 h-4 mr-1.5" />Generate Report
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-hidden flex flex-col max-md:overflow-visible max-md:flex-none">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <SummaryCard label="Current Period" value={data.current_total} sub={data.current_label} color="teal" />
            <SummaryCard label="Previous Period" value={data.previous_total} sub={data.previous_label} color="slate" />
            <ChangeCard curr={data.current_total} prev={data.previous_total} />
            <SummaryCard label="Client Codes" value={data.client_code_data?.length || 0} sub="Unique codes" color="blue" />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-3 bg-slate-100 rounded-lg p-1 w-fit max-md:w-full max-md:overflow-x-auto [&>button]:shrink-0">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`mgmt-tab-${t.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  activeTab === t.id ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              ><t.icon className="w-4 h-4" />{t.label}</button>
            ))}
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-auto bg-white rounded-xl shadow-sm border border-slate-200 max-md:overflow-visible max-md:flex-none" ref={printRef}>
            {activeTab === 'clientCode' && (
              <CompareTab
                title={`Client Code Comparison — ${dateLabel}`}
                rows={data.client_code_data}
                nameKey="code" nameLabel="Client Code"
                trend={data.trend} trendKey="by_client"
                currentLabel={data.current_label} prevLabel={data.previous_label}
                filter={clientFilter} setFilter={setClientFilter}
                selectedItems={selectedClients} setSelectedItems={setSelectedClients}
                searchable
              />
            )}
            {activeTab === 'country' && (
              <CompareTab
                title={`Country Comparison — ${dateLabel}`}
                rows={data.country_data}
                nameKey="country" nameLabel="Country"
                trend={data.trend} trendKey="by_country"
                currentLabel={data.current_label} prevLabel={data.previous_label}
              />
            )}
            {activeTab === 'service' && (
              <CompareTab
                title={`Service Comparison — ${dateLabel}`}
                rows={data.service_data}
                nameKey="service" nameLabel="Service"
                trend={data.trend} trendKey="by_service"
                currentLabel={data.current_label} prevLabel={data.previous_label}
              />
            )}
            {activeTab === 'topCustomers' && (
              <CustomerTab data={data} dateLabel={dateLabel} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Summary Cards ── */
const SummaryCard = ({ label, value, sub, color }) => {
  const colors = { teal: 'bg-teal-50 text-teal-700 border-teal-200', slate: 'bg-slate-50 text-slate-600 border-slate-200', blue: 'bg-blue-50 text-blue-700 border-blue-200' };
  return (
    <div className={`rounded-xl border px-4 py-3 ${colors[color] || colors.teal}`}>
      <div className="text-xs font-medium opacity-80 mb-1">{label}</div>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <div className="text-[10px] opacity-60 mt-0.5 truncate">{sub}</div>}
    </div>
  );
};

const ChangeCard = ({ curr, prev }) => {
  const diff = curr - prev;
  const pct = prev > 0 ? ((diff / prev) * 100).toFixed(1) : curr > 0 ? 100 : 0;
  const up = diff > 0;
  const down = diff < 0;
  return (
    <div className={`rounded-xl border px-4 py-3 ${up ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : down ? 'bg-red-50 text-red-600 border-red-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
      <div className="text-xs font-medium opacity-80 mb-1 flex items-center gap-1">
        Change {up ? <ArrowUpRight className="w-3 h-3" /> : down ? <ArrowDownRight className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
      </div>
      <div className="text-2xl font-bold">{diff > 0 ? '+' : ''}{diff}</div>
      <div className="text-[10px] opacity-60 mt-0.5">{pct > 0 ? '+' : ''}{pct}% vs previous</div>
    </div>
  );
};

/* ── Comparison Tab (Client/Country/Service) ── */
const CompareTab = ({ title, rows, nameKey, nameLabel, trend, trendKey, currentLabel, prevLabel, filter, setFilter, selectedItems, setSelectedItems, searchable }) => {
  const filteredRows = useMemo(() => {
    let r = rows || [];
    if (searchable && selectedItems && selectedItems.length > 0) {
      r = r.filter(row => selectedItems.includes(row[nameKey]));
    }
    if (searchable && filter) {
      const q = filter.toLowerCase();
      r = r.filter(row => row[nameKey].toLowerCase().includes(q));
    }
    return r;
  }, [rows, nameKey, filter, selectedItems, searchable]);

  // Trend chart data
  const trendData = useMemo(() => {
    if (!trend || !trend.labels || trend.labels.length === 0) return [];
    const trendMap = trend[trendKey] || {};
    return trend.labels.map((label, i) => {
      const point = { name: label, Total: trend.total[i] || 0 };
      const entries = searchable && selectedItems?.length > 0
        ? Object.entries(trendMap).filter(([k]) => selectedItems.includes(k))
        : Object.entries(trendMap).slice(0, 6);
      entries.forEach(([key, values]) => { point[key] = values[i] || 0; });
      return point;
    });
  }, [trend, trendKey, searchable, selectedItems]);

  const trendKeys = useMemo(() => {
    if (!trend || !trend[trendKey]) return [];
    const map = trend[trendKey];
    if (searchable && selectedItems?.length > 0) return selectedItems.filter(k => k in map);
    return Object.keys(map).slice(0, 6);
  }, [trend, trendKey, searchable, selectedItems]);

  const toggleItem = (item) => {
    if (!setSelectedItems) return;
    setSelectedItems(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
  };

  const currTotal = filteredRows.reduce((s, r) => s + r.current, 0);
  const prevTotal = filteredRows.reduce((s, r) => s + r.previous, 0);

  return (
    <div className="p-4">
      <div className="text-lg font-semibold text-[#1B4F72] mb-3">{title}</div>

      {/* Search/filter for client codes */}
      {searchable && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text" value={filter || ''} onChange={e => setFilter(e.target.value)}
              placeholder="Search client codes..."
              className="w-full pl-8 pr-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-400"
              data-testid="client-search"
            />
          </div>
          {selectedItems?.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              {selectedItems.map(item => (
                <span key={item} className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                  {item}
                  <X className="w-3 h-3 cursor-pointer hover:text-teal-900" onClick={() => toggleItem(item)} />
                </span>
              ))}
              <button onClick={() => setSelectedItems([])} className="text-xs text-slate-400 hover:text-slate-600 ml-1">Clear all</button>
            </div>
          )}
        </div>
      )}

      {/* Trend Chart */}
      {trendData.length > 1 && (
        <div className="border border-slate-200 rounded-xl p-4 mb-4">
          <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-teal-500" />Trend Over Time
          </h4>
          <ResponsiveContainer width="100%" height={220}>
            {trendKeys.length <= 1 ? (
              <BarChart data={trendData} margin={{ left: 0, right: 8, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="Total" fill="#14b8a6" radius={[4,4,0,0]} />
              </BarChart>
            ) : (
              <LineChart data={trendData} margin={{ left: 0, right: 8, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {trendKeys.map((k, i) => (
                  <Line key={k} type="monotone" dataKey={k} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 2 }} />
                ))}
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {/* Comparison Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse" data-testid={`mgmt-table-${nameKey}`}>
          <thead>
            <tr>
              <th className="bg-[#1B4F72] text-white text-xs font-semibold py-2 px-3 text-left border border-slate-300">{nameLabel}</th>
              <th className="bg-[#1B4F72] text-white text-xs font-semibold py-2 px-3 text-center border border-slate-300">Current</th>
              <th className="bg-[#1B4F72] text-white text-xs font-semibold py-2 px-3 text-center border border-slate-300">Previous</th>
              <th className="bg-[#1B4F72] text-white text-xs font-semibold py-2 px-3 text-center border border-slate-300">Change</th>
              <th className="bg-[#1B4F72] text-white text-xs font-semibold py-2 px-3 text-center border border-slate-300">%</th>
              {searchable && <th className="bg-[#1B4F72] text-white text-xs font-semibold py-2 px-1 text-center border border-slate-300 w-8"></th>}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row, i) => {
              const diff = row.diff;
              const selected = searchable && selectedItems?.includes(row[nameKey]);
              return (
                <tr key={i} className={`hover:bg-slate-50 transition-colors ${selected ? 'bg-teal-50' : ''}`}>
                  <td className="py-1.5 px-3 text-sm font-semibold text-slate-800 border border-slate-200">{row[nameKey]}</td>
                  <td className="py-1.5 px-3 text-sm text-center font-bold text-slate-900 border border-slate-200">{row.current}</td>
                  <td className="py-1.5 px-3 text-sm text-center text-slate-500 border border-slate-200">{row.previous}</td>
                  <td className={`py-1.5 px-3 text-sm text-center font-semibold border border-slate-200 ${diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-red-500' : 'text-slate-400'}`}>
                    {diff > 0 ? '+' : ''}{diff}
                  </td>
                  <td className={`py-1.5 px-3 text-xs text-center border border-slate-200 ${diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-red-500' : 'text-slate-400'}`}>
                    {row.pct_change !== 0 ? `${row.pct_change > 0 ? '+' : ''}${row.pct_change}%` : '—'}
                  </td>
                  {searchable && (
                    <td className="py-1 px-1 text-center border border-slate-200">
                      <input type="checkbox" checked={selected || false} onChange={() => toggleItem(row[nameKey])}
                        className="w-3.5 h-3.5 accent-teal-600 cursor-pointer" />
                    </td>
                  )}
                </tr>
              );
            })}
            {/* Total row */}
            <tr className="bg-[#D5E8D4]">
              <td className="py-2 px-3 text-sm font-bold text-slate-900 border border-slate-300">Total</td>
              <td className="py-2 px-3 text-sm text-center font-bold text-slate-900 border border-slate-300">{currTotal}</td>
              <td className="py-2 px-3 text-sm text-center font-bold text-slate-700 border border-slate-300">{prevTotal}</td>
              <td className={`py-2 px-3 text-sm text-center font-bold border border-slate-300 ${currTotal - prevTotal > 0 ? 'text-emerald-600' : currTotal - prevTotal < 0 ? 'text-red-500' : ''}`}>
                {currTotal - prevTotal > 0 ? '+' : ''}{currTotal - prevTotal}
              </td>
              <td className="py-2 px-3 text-sm text-center border border-slate-300"></td>
              {searchable && <td className="border border-slate-300"></td>}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ── Top 50 Customer Tab ── */
const CustomerTab = ({ data, dateLabel }) => {
  const rows = data.top_customers || [];
  const trendData = useMemo(() => {
    if (!data.trend || !data.trend.labels) return [];
    return data.trend.labels.map((label, i) => ({
      name: label, Total: data.trend.total[i] || 0,
    }));
  }, [data.trend]);

  return (
    <div className="p-4">
      <div className="text-lg font-semibold text-[#1B4F72] mb-3">Top 50 Customers — {dateLabel}</div>

      {trendData.length > 1 && (
        <div className="border border-slate-200 rounded-xl p-4 mb-4">
          <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-teal-500" />Daily Parcel Trend
          </h4>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={trendData} margin={{ left: 0, right: 8, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 10, fill: '#64748b' }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Bar dataKey="Total" fill="#14b8a6" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse" data-testid="mgmt-table-customers">
          <thead>
            <tr>
              <th className="bg-[#1B4F72] text-white text-xs font-semibold py-2 px-2 text-center border border-slate-300 w-8">#</th>
              <th className="bg-[#1B4F72] text-white text-xs font-semibold py-2 px-3 text-left border border-slate-300">Customer Name</th>
              <th className="bg-[#1B4F72] text-white text-xs font-semibold py-2 px-3 text-left border border-slate-300">Country</th>
              <th className="bg-[#1B4F72] text-white text-xs font-semibold py-2 px-3 text-center border border-slate-300">Current</th>
              <th className="bg-[#1B4F72] text-white text-xs font-semibold py-2 px-3 text-center border border-slate-300">Previous</th>
              <th className="bg-[#1B4F72] text-white text-xs font-semibold py-2 px-3 text-center border border-slate-300">Change</th>
              <th className="bg-[#1B4F72] text-white text-xs font-semibold py-2 px-3 text-center border border-slate-300">%</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const diff = row.diff;
              return (
                <tr key={i} className="hover:bg-slate-50 transition-colors">
                  <td className="py-1.5 px-2 text-xs text-center text-slate-400 border border-slate-200">{i+1}</td>
                  <td className="py-1.5 px-3 text-sm font-medium text-slate-800 border border-slate-200">{row.name}</td>
                  <td className="py-1.5 px-3 text-sm text-slate-600 border border-slate-200">{row.country}</td>
                  <td className="py-1.5 px-3 text-sm text-center font-bold text-slate-900 border border-slate-200">{row.current}</td>
                  <td className="py-1.5 px-3 text-sm text-center text-slate-500 border border-slate-200">{row.previous}</td>
                  <td className={`py-1.5 px-3 text-sm text-center font-semibold border border-slate-200 ${diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-red-500' : 'text-slate-400'}`}>
                    {diff > 0 ? '+' : ''}{diff}
                  </td>
                  <td className={`py-1.5 px-3 text-xs text-center border border-slate-200 ${diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-red-500' : 'text-slate-400'}`}>
                    {row.pct_change !== 0 ? `${row.pct_change > 0 ? '+' : ''}${row.pct_change}%` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManagementReports;
